'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function AnnuityPayoutCalculator() {
  const t = useTranslations("tools.annuityPayoutCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const [calculationMode, setCalculationMode] = useState<'fixLength' | 'fixPayment'>('fixLength');
  const [inputs, setInputs] = useState({
    principal: 500000,
    rate: 6,
    years: 10,
    payment: 5000,
    frequency: 12,
  });
  const [results, setResults] = useState<any>(null);
  const [schedule, setSchedule] = useState<any[]>([]);

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const styles = {
    container: getResponsiveStyle(
      { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
      { padding: '1rem' },
      { padding: '1.5rem' },
      { padding: '2rem' }
    ),
    header: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      {},
      { marginBottom: '2.5rem' },
      { marginBottom: '3rem' }
    ),
    h1: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' }
    ),
    summaryText: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6 },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    calculatorTool: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
        borderRadius: '1rem',
        marginBottom: '2rem',
        boxShadow: '0 8px 32px 0 rgba(37, 99, 235, 0.08)',
      },
      { padding: '1.5rem 1rem' },
      { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
      { padding: '3rem 2rem', borderRadius: '1.5rem' }
    ),
    toolContainer: getResponsiveStyle(
      { maxWidth: '100%', margin: '0 auto' },
      {},
      { maxWidth: '900px' },
      { maxWidth: '1100px' }
    ),
    toolHeader: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      {},
      { marginBottom: '2.5rem' },
      {}
    ),
    h2: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    modeTabs: getResponsiveStyle(
      {
        display: 'flex',
        gap: '0.5rem',
        marginBottom: '1.5rem',
        background: '#f1f5f9',
        padding: '0.25rem',
        borderRadius: '0.5rem',
      },
      {},
      {},
      {}
    ),
    modeTab: getResponsiveStyle(
      {
        flex: 1,
        background: 'transparent',
        border: 'none',
        borderRadius: '0.375rem',
        cursor: 'pointer',
        fontWeight: 500,
        color: '#64748b',
        transition: 'all 0.2s',
      },
      { padding: '0.75rem 0.5rem', fontSize: '0.9rem' },
      { padding: '0.75rem 1rem', fontSize: '1rem' },
      {}
    ),
    modeTabActive: {
      background: 'white',
      color: '#2563eb',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    },
    calculatorLayout: getResponsiveStyle(
      { display: 'grid', gap: '2rem', marginBottom: '2rem' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: '1fr 1fr' }
    ),
    calculatorForm: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      {}
    ),
    formGroup: getResponsiveStyle(
      {},
      { marginBottom: '1.25rem' },
      { marginBottom: '1.5rem' },
      {}
    ),
    label: getResponsiveStyle(
      { display: 'block', marginBottom: '0.5rem', color: '#334155', fontWeight: 500 },
      { fontSize: '0.9rem' },
      { fontSize: '0.95rem' },
      {}
    ),
    input: getResponsiveStyle(
      {
        width: '100%',
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        fontSize: '1rem',
        transition: 'all 0.2s',
        boxSizing: 'border-box' as const,
      },
      { padding: '0.75rem 1rem' },
      { padding: '0.875rem 1.25rem' },
      {}
    ),
    select: getResponsiveStyle(
      {
        width: '100%',
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        fontSize: '1rem',
        transition: 'all 0.2s',
        boxSizing: 'border-box' as const,
        background: 'white',
      },
      { padding: '0.75rem 1rem' },
      { padding: '0.875rem 1.25rem' },
      {}
    ),
    inputWithPrefix: {
      position: 'relative' as const,
    },
    inputPrefix: getResponsiveStyle(
      {
        position: 'absolute' as const,
        left: '1rem',
        top: '50%',
        transform: 'translateY(-50%)',
        color: '#64748b',
        fontWeight: 500,
      },
      { fontSize: '1rem' },
      {},
      {}
    ),
    inputSuffix: getResponsiveStyle(
      {
        position: 'absolute' as const,
        right: '1rem',
        top: '50%',
        transform: 'translateY(-50%)',
        color: '#64748b',
        fontWeight: 500,
      },
      { fontSize: '1rem' },
      {},
      {}
    ),
    inputPrefixed: {
      paddingLeft: '2rem',
    },
    inputSuffixed: {
      paddingRight: '3.5rem',
    },
    calculateButton: getResponsiveStyle(
      {
        width: '100%',
        background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '0.5rem',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.3s',
        boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.3)',
      },
      { padding: '1rem', fontSize: '1rem' },
      { padding: '1rem', fontSize: '1.1rem' },
      {}
    ),
    results: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      {}
    ),
    primaryResult: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        color: 'white',
        borderRadius: '0.75rem',
        textAlign: 'center' as const,
        marginBottom: '1.5rem',
      },
      { padding: '1.25rem' },
      { padding: '1.5rem' },
      {}
    ),
    primaryResultLabel: getResponsiveStyle(
      { opacity: 0.9, marginBottom: '0.5rem' },
      { fontSize: '0.85rem' },
      { fontSize: '0.9rem' },
      {}
    ),
    primaryResultValue: getResponsiveStyle(
      { fontWeight: 700 },
      { fontSize: '2rem' },
      { fontSize: '2.25rem' },
      { fontSize: '2.5rem' }
    ),
    resultItem: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '1rem 0',
      borderBottom: '1px solid #e2e8f0',
    },
    resultLabel: getResponsiveStyle(
      { color: '#64748b', fontWeight: 500 },
      { fontSize: '0.9rem' },
      { fontSize: '1rem' },
      {}
    ),
    resultValue: getResponsiveStyle(
      { color: colors.textPrimary, fontWeight: 600 },
      { fontSize: '0.9rem' },
      { fontSize: '1rem' },
      {}
    ),
    chartContainer: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        marginTop: '2rem',
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      {}
    ),
    pieChart: {
      width: '200px',
      height: '200px',
      margin: '0 auto 1.5rem',
    },
    chartLegend: getResponsiveStyle(
      { display: 'flex', justifyContent: 'center', flexWrap: 'wrap' as const },
      { gap: '1rem' },
      { gap: '2rem' },
      {}
    ),
    legendItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    legendColor: {
      width: '1rem',
      height: '1rem',
      borderRadius: '0.25rem',
    },
    legendText: getResponsiveStyle(
      { color: '#64748b' },
      { fontSize: '0.85rem' },
      { fontSize: '0.9rem' },
      {}
    ),
    scheduleTableContainer: {
      maxHeight: '400px',
      overflowY: 'auto' as const,
      marginTop: '1rem',
    },
    scheduleTable: {
      width: '100%',
      borderCollapse: 'collapse' as const,
    },
    tableHeader: getResponsiveStyle(
      {
        background: '#f8fafc',
        textAlign: 'left' as const,
        color: '#475569',
        fontWeight: 600,
        borderBottom: '2px solid #e2e8f0',
      },
      { padding: '0.5rem', fontSize: '0.8rem' },
      { padding: '0.75rem', fontSize: '0.9rem' },
      {}
    ),
    tableCell: getResponsiveStyle(
      {
        borderBottom: '1px solid #e2e8f0',
        color: '#334155',
      },
      { padding: '0.5rem', fontSize: '0.8rem' },
      { padding: '0.75rem', fontSize: '0.9rem' },
      {}
    ),
    glassCard: getResponsiveStyle(
      {
        background: colors.glassBg,
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: `1px solid ${colors.glassBorder}`,
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
        marginBottom: '2rem',
      },
      { padding: '1.5rem' },
      { padding: '2rem', borderRadius: '1.25rem' },
      { padding: '2.5rem', borderRadius: '1.5rem' }
    ),
    paragraph: getResponsiveStyle(
      { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem', lineHeight: 1.7 },
      { fontSize: '1.25rem', lineHeight: 1.8 }
    ),
    list: getResponsiveStyle(
      { marginLeft: '1.5rem', marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    listItem: {
      marginBottom: '0.75rem',
      color: colors.textSecondary,
      lineHeight: 1.6,
    },
    faqItem: getResponsiveStyle(
      {
        background: 'rgba(255, 255, 255, 0.4)',
        borderRadius: '0.75rem',
        marginBottom: '1rem',
      },
      { padding: '1rem' },
      { padding: '1.25rem' },
      { padding: '1.5rem' }
    ),
    faqQuestion: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    faqAnswer: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6 },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' }
    ),
    reference: getResponsiveStyle(
      {
        background: 'rgba(255, 255, 255, 0.4)',
        borderRadius: '0.5rem',
        marginBottom: '0.75rem',
        transition: 'all 0.2s',
        cursor: 'pointer',
      },
      { padding: '0.75rem' },
      { padding: '1rem' },
      {}
    ),
    referenceTitle: getResponsiveStyle(
      { fontWeight: 500, color: colors.primary, marginBottom: '0.25rem' },
      { fontSize: '0.9rem' },
      { fontSize: '1rem' },
      {}
    ),
    referenceUrl: getResponsiveStyle(
      { color: colors.textSecondary, wordBreak: 'break-all' as const },
      { fontSize: '0.8rem' },
      { fontSize: '0.85rem' },
      { fontSize: '0.9rem' }
    ),
  };

  const frequencyNames: { [key: number]: string } = {
    12: t('monthly'),
    24: t('semimonthly'),
    26: t('biweekly'),
    4: t('quarterly'),
    2: t('semiannually'),
    1: t('annually'),
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const principal = inputs.principal;
    const annualRate = inputs.rate / 100;
    const frequency = inputs.frequency;
    const periodicRate = annualRate / frequency;

    if (calculationMode === 'fixLength') {
      const years = inputs.years;
      const totalPeriods = years * frequency;

      if (periodicRate === 0) {
        const payment = principal / totalPeriods;
        const totalPayments = payment * totalPeriods;
        const totalInterest = 0;

        setResults({
          mode: 'fixLength',
          payment,
          totalPeriods,
          totalPayments,
          totalInterest,
          years,
          frequency,
        });

        generateSchedule(principal, periodicRate, payment, frequency, years);
      } else {
        const payment = principal * (periodicRate * Math.pow(1 + periodicRate, totalPeriods)) /
                       (Math.pow(1 + periodicRate, totalPeriods) - 1);

        const totalPayments = payment * totalPeriods;
        const totalInterest = totalPayments - principal;

        setResults({
          mode: 'fixLength',
          payment,
          totalPeriods,
          totalPayments,
          totalInterest,
          years,
          frequency,
        });

        generateSchedule(principal, periodicRate, payment, frequency, years);
      }
    } else {
      const payment = inputs.payment;

      if (payment <= principal * periodicRate && periodicRate > 0) {
        alert(t('paymentTooLowError'));
        return;
      }

      if (periodicRate === 0) {
        const totalPeriods = principal / payment;
        const years = totalPeriods / frequency;
        const totalPayments = payment * totalPeriods;
        const totalInterest = 0;

        setResults({
          mode: 'fixPayment',
          payment,
          totalPeriods,
          totalPayments,
          totalInterest,
          years,
          frequency,
        });

        generateSchedule(principal, periodicRate, payment, frequency, years);
      } else {
        const totalPeriods = Math.log(payment / (payment - principal * periodicRate)) / Math.log(1 + periodicRate);
        const years = totalPeriods / frequency;
        const totalPayments = payment * totalPeriods;
        const totalInterest = totalPayments - principal;

        setResults({
          mode: 'fixPayment',
          payment,
          totalPeriods,
          totalPayments,
          totalInterest,
          years,
          frequency,
        });

        generateSchedule(principal, periodicRate, payment, frequency, years);
      }
    }
  };

  const generateSchedule = (principal: number, periodicRate: number, payment: number, frequency: number, totalYears: number) => {
    const scheduleData: any[] = [];
    let balance = principal;
    const years = Math.ceil(totalYears);

    for (let year = 1; year <= years; year++) {
      const beginningBalance = balance;
      let yearlyInterest = 0;

      for (let period = 0; period < frequency && balance > 0; period++) {
        const periodInterest = balance * periodicRate;
        yearlyInterest += periodInterest;
        balance = balance + periodInterest - payment;

        if (balance < 0) balance = 0;
      }

      scheduleData.push({
        year,
        beginningBalance,
        yearlyInterest,
        endingBalance: balance,
      });

      if (balance <= 0) break;
    }

    setSchedule(scheduleData);
  };

  const describeArc = (x: number, y: number, radius: number, startAngle: number, endAngle: number) => {
    const start = polarToCartesian(x, y, radius, endAngle);
    const end = polarToCartesian(x, y, radius, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

    return [
      "M", x, y,
      "L", start.x, start.y,
      "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y,
      "Z"
    ].join(" ");
  };

  const polarToCartesian = (centerX: number, centerY: number, radius: number, angleInDegrees: number) => {
    const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
    return {
      x: centerX + (radius * Math.cos(angleInRadians)),
      y: centerY + (radius * Math.sin(angleInRadians))
    };
  };

  const formatCurrency = (value: number) => {
    return '$' + value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.h1}>{t('title')}</h1>
        <p style={styles.summaryText}>{t('subtitle')}</p>
      </header>

      <section style={styles.calculatorTool}>
        <div style={styles.toolContainer}>
          <div style={styles.toolHeader}>
            <h2 style={styles.h2}>{t('toolTitle')}</h2>
            <p style={styles.summaryText}>{t('toolSubtitle')}</p>
          </div>

          <div style={styles.modeTabs}>
            <button
              type="button"
              style={{
                ...styles.modeTab,
                ...(calculationMode === 'fixLength' ? styles.modeTabActive : {}),
              }}
              onClick={() => setCalculationMode('fixLength')}
            >
              {t('fixLengthMode')}
            </button>
            <button
              type="button"
              style={{
                ...styles.modeTab,
                ...(calculationMode === 'fixPayment' ? styles.modeTabActive : {}),
              }}
              onClick={() => setCalculationMode('fixPayment')}
            >
              {t('fixPaymentMode')}
            </button>
          </div>

          <div style={styles.calculatorLayout}>
            <div style={styles.calculatorForm}>
              <form onSubmit={handleCalculate}>
                <div style={styles.formGroup}>
                  <label htmlFor="principal" style={styles.label}>{t('principalLabel')}</label>
                  <div style={styles.inputWithPrefix}>
                    <span style={styles.inputPrefix}>$</span>
                    <input
                      type="number"
                      id="principal"
                      value={inputs.principal}
                      onChange={(e) => setInputs({ ...inputs, principal: parseFloat(e.target.value) || 0 })}
                      min="0"
                      step="1000"
                      required
                      style={{ ...styles.input, ...styles.inputPrefixed }}
                    />
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label htmlFor="rate" style={styles.label}>{t('rateLabel')}</label>
                  <div style={styles.inputWithPrefix}>
                    <input
                      type="number"
                      id="rate"
                      value={inputs.rate}
                      onChange={(e) => setInputs({ ...inputs, rate: parseFloat(e.target.value) || 0 })}
                      min="0"
                      max="100"
                      step="0.1"
                      required
                      style={{ ...styles.input, ...styles.inputSuffixed }}
                    />
                    <span style={styles.inputSuffix}>%</span>
                  </div>
                </div>

                {calculationMode === 'fixLength' && (
                  <div style={styles.formGroup}>
                    <label htmlFor="years" style={styles.label}>{t('yearsLabel')}</label>
                    <div style={styles.inputWithPrefix}>
                      <input
                        type="number"
                        id="years"
                        value={inputs.years}
                        onChange={(e) => setInputs({ ...inputs, years: parseFloat(e.target.value) || 0 })}
                        min="1"
                        max="100"
                        step="1"
                        required
                        style={{ ...styles.input, ...styles.inputSuffixed }}
                      />
                      <span style={styles.inputSuffix}>{t('yearsUnit')}</span>
                    </div>
                  </div>
                )}

                {calculationMode === 'fixPayment' && (
                  <div style={styles.formGroup}>
                    <label htmlFor="payment" style={styles.label}>{t('paymentLabel')}</label>
                    <div style={styles.inputWithPrefix}>
                      <span style={styles.inputPrefix}>$</span>
                      <input
                        type="number"
                        id="payment"
                        value={inputs.payment}
                        onChange={(e) => setInputs({ ...inputs, payment: parseFloat(e.target.value) || 0 })}
                        min="0"
                        step="100"
                        required
                        style={{ ...styles.input, ...styles.inputPrefixed }}
                      />
                    </div>
                  </div>
                )}

                <div style={styles.formGroup}>
                  <label htmlFor="frequency" style={styles.label}>{t('frequencyLabel')}</label>
                  <select
                    id="frequency"
                    value={inputs.frequency}
                    onChange={(e) => setInputs({ ...inputs, frequency: parseInt(e.target.value) })}
                    required
                    style={styles.select}
                  >
                    <option value="12">{t('monthly')}</option>
                    <option value="24">{t('semimonthly')}</option>
                    <option value="26">{t('biweekly')}</option>
                    <option value="4">{t('quarterly')}</option>
                    <option value="2">{t('semiannually')}</option>
                    <option value="1">{t('annually')}</option>
                  </select>
                </div>

                <button type="submit" style={styles.calculateButton}>
                  {t('calculateButton')}
                </button>
              </form>
            </div>

            {results && (
              <div style={styles.results}>
                <h3 style={styles.h2}>{t('resultsTitle')}</h3>
                <div style={styles.primaryResult}>
                  <div style={styles.primaryResultLabel}>
                    {results.mode === 'fixLength' ? t('youCanWithdraw') : t('yourAnnuityWillLast')}
                  </div>
                  <div style={styles.primaryResultValue}>
                    {results.mode === 'fixLength'
                      ? formatCurrency(results.payment)
                      : results.years.toFixed(2)
                    }
                  </div>
                  <div style={styles.primaryResultLabel}>
                    {results.mode === 'fixLength'
                      ? frequencyNames[results.frequency]
                      : t('yearsUnit')
                    }
                  </div>
                </div>

                <div style={styles.resultItem}>
                  <span style={styles.resultLabel}>
                    {t('totalPaymentsLabel', { count: Math.ceil(results.totalPeriods) })}
                  </span>
                  <span style={styles.resultValue}>{formatCurrency(results.totalPayments)}</span>
                </div>
                <div style={styles.resultItem}>
                  <span style={styles.resultLabel}>{t('totalInterestLabel')}</span>
                  <span style={styles.resultValue}>{formatCurrency(results.totalInterest)}</span>
                </div>
                {results.mode === 'fixPayment' && (
                  <div style={styles.resultItem}>
                    <span style={styles.resultLabel}>{t('yearsUntilDepletedLabel')}</span>
                    <span style={styles.resultValue}>{results.years.toFixed(2)} {t('yearsUnit')}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {results && (
            <>
              <div style={styles.chartContainer}>
                <h3 style={styles.h2}>{t('paymentBreakdownTitle')}</h3>
                <svg style={styles.pieChart} viewBox="0 0 200 200">
                  <circle cx="100" cy="100" r="90" fill="#f1f5f9"/>
                  <path
                    d={describeArc(100, 100, 90, 0, (inputs.principal / (inputs.principal + results.totalInterest)) * 360)}
                    fill="#2563eb"
                  />
                  <path
                    d={describeArc(100, 100, 90, (inputs.principal / (inputs.principal + results.totalInterest)) * 360, 360)}
                    fill="#10b981"
                  />
                  <circle cx="100" cy="100" r="50" fill="white"/>
                </svg>
                <div style={styles.chartLegend}>
                  <div style={styles.legendItem}>
                    <div style={{ ...styles.legendColor, background: '#2563eb' }}></div>
                    <span style={styles.legendText}>
                      {t('startingPrincipal')} ({((inputs.principal / (inputs.principal + results.totalInterest)) * 100).toFixed(0)}%)
                    </span>
                  </div>
                  <div style={styles.legendItem}>
                    <div style={{ ...styles.legendColor, background: '#10b981' }}></div>
                    <span style={styles.legendText}>
                      {t('interestReturn')} ({((results.totalInterest / (inputs.principal + results.totalInterest)) * 100).toFixed(0)}%)
                    </span>
                  </div>
                </div>
              </div>

              <div style={styles.chartContainer}>
                <h3 style={styles.h2}>{t('paymentScheduleTitle')}</h3>
                <div style={styles.scheduleTableContainer}>
                  <table style={styles.scheduleTable}>
                    <thead>
                      <tr>
                        <th style={styles.tableHeader}>{t('yearColumn')}</th>
                        <th style={styles.tableHeader}>{t('beginningBalanceColumn')}</th>
                        <th style={styles.tableHeader}>{t('interestReturnColumn')}</th>
                        <th style={styles.tableHeader}>{t('endingBalanceColumn')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {schedule.map((row) => (
                        <tr key={row.year}>
                          <td style={styles.tableCell}>{row.year}</td>
                          <td style={styles.tableCell}>{formatCurrency(row.beginningBalance)}</td>
                          <td style={styles.tableCell}>{formatCurrency(row.yearlyInterest)}</td>
                          <td style={styles.tableCell}>{formatCurrency(row.endingBalance)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('introTitle')}</h2>
        <p style={styles.paragraph}>{t('introParagraph1')}</p>
        <p style={styles.paragraph}>{t('introParagraph2')}</p>
        <p style={styles.paragraph}>{t('introParagraph3')}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('howToUseTitle')}</h2>
        <ol style={styles.list}>
          <li style={styles.listItem}>{t('howToUseStep1')}</li>
          <li style={styles.listItem}>{t('howToUseStep2')}</li>
          <li style={styles.listItem}>{t('howToUseStep3')}</li>
          <li style={styles.listItem}>{t('howToUseStep4')}</li>
          <li style={styles.listItem}>{t('howToUseStep5')}</li>
          <li style={styles.listItem}>{t('howToUseStep6')}</li>
        </ol>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('insightsTitle')}</h2>
        <h3 style={{ ...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.5rem' : '1.75rem' }}>
          {t('insightsSubtitle1')}
        </h3>
        <p style={styles.paragraph}>{t('insightsParagraph1')}</p>
        <p style={styles.paragraph}>{t('insightsParagraph2')}</p>

        <h3 style={{ ...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.5rem' : '1.75rem' }}>
          {t('insightsSubtitle2')}
        </h3>
        <p style={styles.paragraph}>{t('insightsParagraph3')}</p>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t('insightsBullet1')}</li>
          <li style={styles.listItem}>{t('insightsBullet2')}</li>
          <li style={styles.listItem}>{t('insightsBullet3')}</li>
          <li style={styles.listItem}>{t('insightsBullet4')}</li>
        </ul>

        <h3 style={{ ...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.5rem' : '1.75rem' }}>
          {t('insightsSubtitle3')}
        </h3>
        <p style={styles.paragraph}>{t('insightsParagraph4')}</p>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t('insightsBullet5')}</li>
          <li style={styles.listItem}>{t('insightsBullet6')}</li>
          <li style={styles.listItem}>{t('insightsBullet7')}</li>
          <li style={styles.listItem}>{t('insightsBullet8')}</li>
        </ul>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('detailsTitle')}</h2>
        <h3 style={{ ...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.5rem' : '1.75rem' }}>
          {t('detailsSubtitle1')}
        </h3>
        <p style={styles.paragraph}>{t('detailsParagraph1')}</p>
        <p style={styles.paragraph}>{t('detailsParagraph2')}</p>

        <h3 style={{ ...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.5rem' : '1.75rem' }}>
          {t('detailsSubtitle2')}
        </h3>
        <p style={styles.paragraph}>{t('detailsParagraph3')}</p>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t('detailsBullet1')}</li>
          <li style={styles.listItem}>{t('detailsBullet2')}</li>
          <li style={styles.listItem}>{t('detailsBullet3')}</li>
        </ul>

        <h3 style={{ ...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.5rem' : '1.75rem' }}>
          {t('detailsSubtitle3')}
        </h3>
        <p style={styles.paragraph}>{t('detailsParagraph4')}</p>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t('detailsBullet4')}</li>
          <li style={styles.listItem}>{t('detailsBullet5')}</li>
          <li style={styles.listItem}>{t('detailsBullet6')}</li>
        </ul>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('faqTitle')}</h2>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faqQuestion1')}</h3>
          <p style={styles.faqAnswer}>{t('faqAnswer1')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faqQuestion2')}</h3>
          <p style={styles.faqAnswer}>{t('faqAnswer2')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faqQuestion3')}</h3>
          <p style={styles.faqAnswer}>{t('faqAnswer3')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faqQuestion4')}</h3>
          <p style={styles.faqAnswer}>{t('faqAnswer4')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faqQuestion5')}</h3>
          <p style={styles.faqAnswer}>{t('faqAnswer5')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faqQuestion6')}</h3>
          <p style={styles.faqAnswer}>{t('faqAnswer6')}</p>
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('referencesTitle')}</h2>

        <div style={styles.reference} onClick={() => window.open('https://www.vedantu.com/calculator/annuity-payout', '_blank')}>
          <div style={styles.referenceTitle}>{t('reference1Title')}</div>
          <div style={styles.referenceUrl}>https://www.vedantu.com/calculator/annuity-payout</div>
        </div>

        <div style={styles.reference} onClick={() => window.open('https://www.annuitywatchusa.com/how-to-calculate-annuity-payout/', '_blank')}>
          <div style={styles.referenceTitle}>{t('reference2Title')}</div>
          <div style={styles.referenceUrl}>https://www.annuitywatchusa.com/how-to-calculate-annuity-payout/</div>
        </div>

        <div style={styles.reference} onClick={() => window.open('https://www.bankrate.com/investing/annuity-calculator/', '_blank')}>
          <div style={styles.referenceTitle}>{t('reference3Title')}</div>
          <div style={styles.referenceUrl}>https://www.bankrate.com/investing/annuity-calculator/</div>
        </div>

        <div style={styles.reference} onClick={() => window.open('https://dechtmanwealth.com/insights/blog/annuity-payout-rate-and-average-returns', '_blank')}>
          <div style={styles.referenceTitle}>{t('reference4Title')}</div>
          <div style={styles.referenceUrl}>https://dechtmanwealth.com/insights/blog/annuity-payout-rate-and-average-returns</div>
        </div>

        <div style={styles.reference} onClick={() => window.open('https://www.tiaa.org/public/tools-calculators/annuity-payout', '_blank')}>
          <div style={styles.referenceTitle}>{t('reference5Title')}</div>
          <div style={styles.referenceUrl}>https://www.tiaa.org/public/tools-calculators/annuity-payout</div>
        </div>
      </section>
    </div>
  );
}